import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { changePassword, validatePasswordStrength } from '../../services/passwordService';
import { useAuth } from '../../contexts/AuthContext';
import './ChangePasswordModal.css';

const ChangePasswordModal = ({ onSuccess, onCancel = null }) => {
  const { currentUser } = useAuth();
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentUser) {
      const googleProvider = currentUser.providerData.find(p => p.providerId === 'google.com');
      setIsGoogleUser(!!googleProvider);
    }
  }, [currentUser]);

  const passwordStrength = validatePasswordStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validações
    if (!isGoogleUser && !currentPassword) {
      setError('Digite sua senha atual');
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError('Preencha todos os campos');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (!passwordStrength.isValid) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (newPassword === currentPassword) {
      setError('A nova senha deve ser diferente da senha atual');
      return;
    }

    setLoading(true);

    try {
      const result = await changePassword(currentPassword || '', newPassword);

      if (result.success) {
        alert('✅ Senha alterada com sucesso!');
        onSuccess();
      } else {
        setError(result.error || 'Erro ao alterar senha');
      }
    } catch (err) {
      setError('Erro ao alterar senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = () => {
    switch (passwordStrength.strength) {
      case 'strong': return '#16a34a';
      case 'medium': return '#eab308';
      default: return '#dc2626';
    }
  };

  const getStrengthLabel = () => {
    switch (passwordStrength.strength) {
      case 'strong': return 'Forte';
      case 'medium': return 'Média';
      default: return 'Fraca';
    }
  };

  return (
    <div className="password-modal-overlay">
      <div className="password-modal-content">
        <div className="password-modal-header">
          <div className="password-modal-icon">
            <Lock size={32} />
          </div>
          <h2>Alterar Senha</h2>
          <p className="password-modal-description">
            Por segurança, você precisa alterar sua senha temporária antes de continuar.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="password-form">
          {error && (
            <div className="password-alert error">
              <AlertCircle size={20} />
              {error}
            </div>
          )}

          {isGoogleUser && (
            <div className="password-alert info">
              <Info size={20} />
              Você usa login com Google. Você pode definir uma senha adicional ou pular esta etapa.
            </div>
          )}

          {!isGoogleUser && (
            <div className="password-form-group">
              <label>Senha Atual</label>
              <div className="password-input-wrapper">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Digite sua senha temporária"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  tabIndex={-1}
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          )}

          <div className="password-form-group">
            <label>Nova Senha</label>
            <div className="password-input-wrapper">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Digite sua nova senha"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowNewPassword(!showNewPassword)}
                tabIndex={-1}
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {newPassword && (
              <div className="password-strength">
                <div className="password-strength-bar">
                  <div
                    className="password-strength-fill"
                    style={{
                      width: `${(Object.values(passwordStrength.validations).filter(Boolean).length / 5) * 100}%`,
                      backgroundColor: getStrengthColor()
                    }}
                  />
                </div>
                <span className="password-strength-label" style={{ color: getStrengthColor() }}>
                  Força: {getStrengthLabel()}
                </span>
              </div>
            )}

            <div className="password-requirements">
              <div className={passwordStrength.validations.length ? 'valid' : ''}>
                {passwordStrength.validations.length ? <CheckCircle2 size={14} /> : <span>○</span>}
                Mínimo 8 caracteres
              </div>
              <div className={passwordStrength.validations.upperCase ? 'valid' : ''}>
                {passwordStrength.validations.upperCase ? <CheckCircle2 size={14} /> : <span>○</span>}
                Letra maiúscula
              </div>
              <div className={passwordStrength.validations.lowerCase ? 'valid' : ''}>
                {passwordStrength.validations.lowerCase ? <CheckCircle2 size={14} /> : <span>○</span>}
                Letra minúscula
              </div>
              <div className={passwordStrength.validations.numbers ? 'valid' : ''}>
                {passwordStrength.validations.numbers ? <CheckCircle2 size={14} /> : <span>○</span>}
                Número
              </div>
              <div className={passwordStrength.validations.specialChar ? 'valid' : ''}>
                {passwordStrength.validations.specialChar ? <CheckCircle2 size={14} /> : <span>○</span>}
                Caractere especial
              </div>
            </div>
          </div>

          <div className="password-form-group">
            <label>Confirmar Nova Senha</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Digite novamente a nova senha"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <span className="password-error-text">As senhas não coincidem</span>
            )}
          </div>

          <div className="password-form-actions">
            {onCancel && (
              <button
                type="button"
                className="password-btn-cancel"
                onClick={onCancel}
                disabled={loading}
              >
                {isGoogleUser ? 'Pular' : 'Cancelar'}
              </button>
            )}
            <button
              type="submit"
              className="password-btn-submit"
              disabled={loading || !passwordStrength.isValid || newPassword !== confirmPassword || (!isGoogleUser && !currentPassword)}
            >
              {loading ? 'Alterando...' : 'Alterar Senha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
